// ═══════════════════════════════════════════════════════════════════════════════
// AUTH STORE — Session Management for Client Portal & Admin Dashboard
// EnergiPro Solutions — Secure Access Control
// ═══════════════════════════════════════════════════════════════════════════════

import { BACKEND_URL } from '../config/api';
const STORAGE_KEY = 'energipro_session';
const INACTIVITY_TIMEOUT = 15 * 60 * 1000; // 15 minutes in ms

export interface AuthSession {
  token: string;
  email: string;
  role: 'client' | 'admin';
  expiresAt: number;
  lastActivity: number;
}

// ─── Session Persistence ─────────────────────────────────────────────────────

export function getStoredSession(): AuthSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const session: AuthSession = JSON.parse(raw);
    
    // Check if session has expired
    const now = Date.now();
    if (session.expiresAt * 1000 < now) {
      clearStoredSession();
      return null;
    }
    
    // Check inactivity timeout
    if (now - session.lastActivity > INACTIVITY_TIMEOUT) {
      clearStoredSession();
      return null;
    }
    
    return session;
  } catch {
    return null;
  }
}

export function setStoredSession(session: AuthSession): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

export function clearStoredSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

export function updateLastActivity(): void {
  const session = getStoredSession();
  if (session) {
    session.lastActivity = Date.now();
    setStoredSession(session);
  }
}

// ─── API Methods ─────────────────────────────────────────────────────────────

export async function requestOTP(email: string, role: 'client' | 'admin' = 'client'): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const res = await fetch(`${BACKEND_URL}/api/public/auth/request-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, role }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Failed to send verification code' };
    }
    return { success: true, message: data.message };
  } catch (err: any) {
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function verifyOTP(email: string, otp: string): Promise<{ success: boolean; session?: AuthSession; error?: string }> {
  try {
    const res = await fetch(`${BACKEND_URL}/api/public/auth/verify-otp`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });
    const data = await res.json();
    if (!res.ok) {
      return { success: false, error: data.error || 'Invalid verification code' };
    }
    const session: AuthSession = {
      token: data.token,
      email: data.email,
      role: data.role,
      expiresAt: data.expiresAt,
      lastActivity: Date.now(),
    };
    setStoredSession(session);
    return { success: true, session };
  } catch (err: any) {
    return { success: false, error: 'Network error. Please try again.' };
  }
}

export async function verifySession(token: string): Promise<{ valid: boolean; email?: string; role?: string }> {
  try {
    const res = await fetch(`${BACKEND_URL}/api/public/auth/verify-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    });
    const data = await res.json();
    if (!res.ok) {
      clearStoredSession();
      return { valid: false };
    }
    updateLastActivity();
    return { valid: true, email: data.email, role: data.role };
  } catch {
    return { valid: false };
  }
}

export async function logout(): Promise<void> {
  const session = getStoredSession();
  if (session) {
    try {
      await fetch(`${BACKEND_URL}/api/public/auth/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: session.token }),
      });
    } catch {
      // Ignore errors on logout
    }
  }
  clearStoredSession();
}

// ─── Inactivity Monitor ──────────────────────────────────────────────────────

let inactivityTimer: ReturnType<typeof setTimeout> | null = null;
let onSessionExpired: (() => void) | null = null;

export function startInactivityMonitor(onExpire: () => void): void {
  onSessionExpired = onExpire;
  resetInactivityTimer();
  
  // Track user activity
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
  events.forEach(event => {
    document.addEventListener(event, handleActivity, { passive: true });
  });
}

export function stopInactivityMonitor(): void {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
    inactivityTimer = null;
  }
  const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
  events.forEach(event => {
    document.removeEventListener(event, handleActivity);
  });
}

function handleActivity(): void {
  updateLastActivity();
  resetInactivityTimer();
}

function resetInactivityTimer(): void {
  if (inactivityTimer) {
    clearTimeout(inactivityTimer);
  }
  inactivityTimer = setTimeout(() => {
    if (onSessionExpired) {
      clearStoredSession();
      onSessionExpired();
    }
  }, INACTIVITY_TIMEOUT);
}
